// src/components/Layout.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  User,
  Settings,
  HelpCircle,
  LogOut,
  Menu,
  X,
  Users,
  Home,
  DollarSign,
  Info,
  MessageSquare,
  Bell,
  Search,
  Sparkles,
  Compass,
  BookOpen,
  BarChart3,
  Megaphone,
  Globe,
  Star,
  Calendar,
} from 'lucide-react';
import Logo from '../assets/divine-frameworks-logo.svg';
import LogoWhite from '../assets/divine-frameworks-logo-white.svg';
import { db, auth } from '../config/firebase-config';
import { collection, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import firestoreService from '../services/firestore-service';
import authService from '../services/auth-service';
import PausedProfileBanner from './PausedProfileBanner';
import AdminTierOverride from './AdminTierOverride';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => localStorage.getItem('isAuthenticated') === 'true'
  );
  const [userInfo, setUserInfo] = useState(() => {
    if (localStorage.getItem('isAuthenticated') !== 'true') return null;
    return {
      name: localStorage.getItem('userName') || 'User',
      email: localStorage.getItem('userEmail') || '',
      role: localStorage.getItem('userRole') || 'practitioner',
      photo: null,
    };
  });
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isPaused, setIsPaused] = useState(() => {
    return localStorage.getItem('profilePaused') === 'true';
  });
  // eslint-disable-next-line no-unused-vars
  const [_tierOverride, setTierOverride] = useState(null);

  // Ref for profile dropdown click-outside detection
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };

    if (showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileDropdown]);

  useEffect(() => {
    // Check authentication status
    const authStatus = localStorage.getItem('isAuthenticated') === 'true';
    setIsAuthenticated(authStatus);

    if (authStatus) {
      // Try to get name from wizard data first, then fall back to userName
      let displayName = localStorage.getItem('userName') || 'User';
      const setupData = localStorage.getItem('practitionerSetup');
      if (setupData) {
        try {
          const parsed = JSON.parse(setupData);
          displayName = parsed.wizardData?.profile?.displayName || parsed.wizardData?.profile?.firstName || displayName;
        } catch (e) {
          console.warn('Failed to parse practitionerSetup:', e);
        }
      }

      // Resolve profile photo from multiple localStorage sources
      let profilePhoto = null;
      try {
        const setup = JSON.parse(localStorage.getItem('practitioner_setup') || '{}');
        profilePhoto = setup?.profile?.photo || setup?.profile?.profilePhoto || null;
      } catch (e) { /* ignore */ }
      if (!profilePhoto) {
        try {
          const profile = JSON.parse(localStorage.getItem('practitioner_profile') || '{}');
          profilePhoto = profile?.profilePhoto || profile?.photo || null;
        } catch (e) { /* ignore */ }
      }
      // Only store non-Base64 URLs (Base64 strings are too large for state and shouldn't be in nav)
      if (profilePhoto && profilePhoto.startsWith('data:')) profilePhoto = null;

      setUserInfo({
        name: displayName,
        email: localStorage.getItem('userEmail') || 'user@example.com',
        role: localStorage.getItem('userRole') || 'practitioner',
        photo: profilePhoto || null,
      });
    }

    // Handle scroll effect
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location]); // Re-check auth on route change

  // Listen for unread messages count — reads from practitioners/{uid}/conversations subcollection
  useEffect(() => {
    if (!isAuthenticated || !db || !auth) {
      setUnreadCount(0);
      return;
    }

    let done = false;
    let convUnsub = null;
    const timeout = setTimeout(() => { if (!done) { done = true; setUnreadCount(0); } }, 3000);

    const authUnsub = onAuthStateChanged(auth, (user) => {
      if (done) return;
      if (user) {
        done = true;
        clearTimeout(timeout);
        const convsRef = collection(db, 'practitioners', user.uid, 'conversations');
        convUnsub = onSnapshot(
          convsRef,
          (snap) => {
            let total = 0;
            snap.forEach((d) => { total += d.data().unreadCount || 0; });
            setUnreadCount(total);
          },
          (err) => { console.warn('Layout: unread count listener error:', err.message); }
        );
      } else {
        // user null — wait for real user (Firebase fires null first on cold start)
      }
    });

    return () => {
      done = true;
      clearTimeout(timeout);
      authUnsub();
      if (convUnsub) convUnsub();
    };
  }, [isAuthenticated]);

  const handleLogout = async () => {
    try {
      await authService.signOut();
    } catch (err) {
      console.warn('Sign out failed:', err);
    }
    localStorage.clear();
    setIsAuthenticated(false);
    setUserInfo(null);
    navigate('/');
  };

  // Listen for changes to pause state (from other components)
  useEffect(() => {
    const handleStorageChange = () => {
      const paused = localStorage.getItem('profilePaused') === 'true';
      setIsPaused(paused);
    };

    // Listen for storage events (cross-tab)
    window.addEventListener('storage', handleStorageChange);

    // Listen for custom event (same tab)
    window.addEventListener('profilePauseChanged', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('profilePauseChanged', handleStorageChange);
    };
  }, []);

  // Firestore fallback on mount if localStorage has no pause state (new device)
  useEffect(() => {
    const savedPaused = localStorage.getItem('profilePaused');
    if (savedPaused === null) {
      const userId = firestoreService.getCurrentUserId();
      if (userId) {
        firestoreService.getProfileStatus(userId).then(firestoreData => {
          if (firestoreData) {
            const paused = firestoreData.paused || false;
            localStorage.setItem('profilePaused', String(paused));
            if (firestoreData.pausedDate) {
              localStorage.setItem('profilePausedDate', firestoreData.pausedDate);
            }
            setIsPaused(paused);
          }
        }).catch(err => console.warn('Firestore profile status read failed:', err));
      }
    }
  }, []);

  const handleReactivate = () => {
    setIsPaused(false);
    localStorage.setItem('profilePaused', 'false');
    localStorage.removeItem('profilePausedDate');
    window.dispatchEvent(new Event('profilePauseChanged'));
    // Background sync to Firestore
    const userId = firestoreService.getCurrentUserId();
    if (userId) {
      firestoreService.updateProfileStatus(userId, { paused: false, pausedDate: null }).catch(err => {
        console.warn('⚠️ Firestore profile status save failed:', err);
      });
    }
  };

  const getQuickLinks = () => [
    { icon: User, label: 'My Profile', path: '/dashboard/settings/profile' },
    { icon: MessageSquare, label: 'Messages', path: '/messages' },
    { icon: Users, label: 'Clients', path: '/dashboard/clients' },
    { icon: Star, label: 'Reviews', path: '/dashboard/reviews' },
    { icon: BarChart3, label: 'Analytics', path: '/dashboard/analytics' },
    { icon: DollarSign, label: 'Earnings', path: '/dashboard/earnings' },
  ];

  const isActivePath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Responsive nav classes — using injected style to avoid Tailwind purge issues */}
      <style>{`
        /* Hamburger: visible on mobile, hidden on desktop */
        .df-nav-hamburger { display: flex; align-items: center; justify-content: center; }
        @media (min-width: 768px) { .df-nav-hamburger { display: none !important; } }

        /* Desktop nav links: hidden on mobile, flex on desktop */
        .df-nav-desktop { display: none !important; }
        @media (min-width: 768px) { .df-nav-desktop { display: flex !important; } }

        /* Auth buttons (Sign In / Get Started): hidden on mobile, flex on desktop */
        .df-nav-auth-btns { display: none !important; }
        @media (min-width: 768px) { .df-nav-auth-btns { display: flex !important; } }

        /* Search icon: hidden on mobile, flex on sm+ */
        .df-nav-search { display: none !important; }
        @media (min-width: 640px) { .df-nav-search { display: flex !important; } }

        /* Brand name text: hidden below 360px (very small screens only), visible on 360px+ */
        .df-nav-brand-text { display: none !important; }
        @media (min-width: 360px) { .df-nav-brand-text { display: inline !important; } }

        /* Username in avatar button: hidden below lg */
        .df-nav-username { display: none !important; }
        @media (min-width: 1024px) { .df-nav-username { display: block !important; } }

        /* Mobile menu: smooth slide-down */
        @keyframes df-menu-in {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .df-mobile-menu-panel {
          animation: df-menu-in 0.18s ease-out;
          box-shadow: 0 8px 24px -4px rgba(0,0,0,0.16), 0 4px 8px -2px rgba(0,0,0,0.08);
        }
      `}</style>

      {/* Navigation Bar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white'
      } border-b border-[#E5E7EB]`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center space-x-2 sm:space-x-3 min-w-0"
            >
              <img src={Logo} alt="Divine Frameworks" className="w-auto flex-shrink-0" style={{ height: '85px', width: 'auto' }} />
              <span className="df-nav-brand-text text-sm md:text-xl font-medium text-black uppercase tracking-wide truncate">
                Divine Frameworks
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="df-nav-desktop items-center space-x-1">
              {isAuthenticated && userInfo?.role !== 'seeker' ? (
                // Authenticated non-seeker navigation (practitioners, admins, pending users)
                <>
                  <Link
                    to="/dashboard/practitioner"
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/dashboard/practitioner') || location.pathname === '/dashboard'
                        ? 'bg-purple-50 text-purple-600'
                        : 'text-black hover:text-df-gold'
                    }`}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/dashboard/calendar"
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/dashboard/calendar')
                        ? 'bg-purple-50 text-purple-600'
                        : 'text-black hover:text-df-gold'
                    }`}
                  >
                    Calendar
                  </Link>
                  <Link
                    to="/dashboard/clients"
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/dashboard/clients')
                        ? 'bg-purple-50 text-purple-600'
                        : 'text-black hover:text-df-gold'
                    }`}
                  >
                    Clients
                  </Link>
                  <Link
                    to="/messages"
                    className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                      isActivePath('/messages')
                        ? 'bg-purple-50 text-purple-600'
                        : 'text-black hover:text-df-gold'
                    }`}
                  >
                    Messages
                    {unreadCount > 0 && (
                      <span className="px-2 py-0.5 bg-purple-600 text-white text-xs font-bold rounded-full min-w-[20px] text-center">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/business-playbook"
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/business-playbook')
                        ? 'bg-purple-50 text-purple-600'
                        : 'text-black hover:text-df-gold'
                    }`}
                  >
                    Playbook
                  </Link>
                </>
              ) : isAuthenticated ? (
                // Authenticated Seeker Navigation
                <>
                  <Link
                    to="/my-sessions"
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/my-sessions')
                        ? 'bg-amber-50 text-[#bb9749]'
                        : 'text-black hover:text-df-gold'
                    }`}
                  >
                    My Sessions
                  </Link>
                  <Link
                    to="/directory"
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/directory')
                        ? 'bg-amber-50 text-[#bb9749]'
                        : 'text-black hover:text-df-gold'
                    }`}
                  >
                    Browse
                  </Link>
                  <Link
                    to="/my-sessions?tab=saved"
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      location.search.includes('tab=saved')
                        ? 'bg-amber-50 text-[#bb9749]'
                        : 'text-black hover:text-df-gold'
                    }`}
                  >
                    Favorites
                  </Link>
                  <Link
                    to="/learn"
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/learn')
                        ? 'bg-amber-50 text-[#bb9749]'
                        : 'text-black hover:text-df-gold'
                    }`}
                  >
                    Learn
                  </Link>
                </>
              ) : (
                // Public Navigation
                <>
                  <Link
                    to="/directory"
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/directory')
                        ? 'bg-df-gold-wash text-df-gold'
                        : 'text-black hover:text-df-gold'
                    }`}
                  >
                    Practitioners
                  </Link>
                  <Link
                    to="/pricing"
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/pricing')
                        ? 'bg-df-gold-wash text-df-gold'
                        : 'text-black hover:text-df-gold'
                    }`}
                  >
                    Pricing
                  </Link>
                  <Link
                    to="/about"
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/about')
                        ? 'bg-df-gold-wash text-df-gold'
                        : 'text-black hover:text-df-gold'
                    }`}
                  >
                    About
                  </Link>
                </>
              )}
            </div>

            {/* Right Side - Auth/Profile */}
            <div className="flex items-center space-x-4">
              {/* Search Button */}
              <button
                onClick={() => navigate('/search')}
                className="df-nav-search p-2 text-black hover:text-df-gold hover:bg-df-gold-wash rounded-lg transition-all items-center justify-center"
              >
                <Search className="w-5 h-5" />
              </button>

              {isAuthenticated ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                    className="flex items-center space-x-3 p-2 rounded-xl hover:bg-[#F9FAFB] transition-all"
                    data-tour="settings"
                  >
                    <div className="flex items-center space-x-2">
                      {userInfo?.photo ? (
                        <img src={userInfo.photo} alt={userInfo.name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                          style={{
                            background: 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #bb9749, #4c1d95, #22d3ee, #6366f1, #bb9749, #7c3aed)',
                            backgroundSize: '400% 400%',
                            animation: 'auroraShift 16s ease infinite',
                          }}
                        >
                          {userInfo?.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="df-nav-username text-left">
                        <div className="text-sm font-medium text-[#111827]">{userInfo?.name}</div>
                      </div>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-[#6B7280] transition-transform ${showProfileDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Profile Dropdown */}
                  {showProfileDropdown && (
                    <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-[#E5E7EB] overflow-hidden animate-fadeIn">
                      {/* User Info Header */}
                      <div className="p-4 bg-gradient-to-br from-df-purple-wash to-df-gold-wash">
                        <div className="flex items-center space-x-3">
                          {userInfo?.photo ? (
                            <img src={userInfo.photo} alt={userInfo.name} className="w-12 h-12 rounded-full object-cover" />
                          ) : (
                            <div
                              className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                              style={{
                                background: 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #bb9749, #4c1d95, #22d3ee, #6366f1, #bb9749, #7c3aed)',
                                backgroundSize: '400% 400%',
                                animation: 'auroraShift 16s ease infinite',
                              }}
                            >
                              {userInfo?.name?.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="font-semibold text-[#111827]">{userInfo?.name}</div>
                            <div className="text-sm text-[#6B7280]">{userInfo?.email}</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Quick Links — role-aware */}
                      {userInfo?.role === 'seeker' ? (
                        // Seeker dropdown
                        <div className="py-2">
                          <button
                            onClick={() => { navigate('/my-sessions'); setShowProfileDropdown(false); }}
                            className="w-full px-4 py-3 text-left flex items-center space-x-3 hover:bg-df-gold-wash transition-colors group"
                          >
                            <Calendar className="w-5 h-5 text-[#6B7280] group-hover:text-df-gold" />
                            <span className="text-[#111827] group-hover:text-df-gold">My Sessions</span>
                          </button>
                          <button
                            onClick={() => { navigate('/directory'); setShowProfileDropdown(false); }}
                            className="w-full px-4 py-3 text-left flex items-center space-x-3 hover:bg-df-gold-wash transition-colors group"
                          >
                            <Search className="w-5 h-5 text-[#6B7280] group-hover:text-df-gold" />
                            <span className="text-[#111827] group-hover:text-df-gold">Browse Practitioners</span>
                          </button>
                          <div className="border-t border-[#E5E7EB] my-1 mx-4" />
                          <button
                            onClick={() => { navigate('/help'); setShowProfileDropdown(false); }}
                            className="w-full px-4 py-3 text-left flex items-center space-x-3 hover:bg-[#FBF7EF] transition-colors group"
                          >
                            <HelpCircle className="w-5 h-5 text-[#6B7280] group-hover:text-[#bb9749]" />
                            <span className="text-[#111827] group-hover:text-[#bb9749]">Help</span>
                          </button>
                          <div className="border-t border-[#E5E7EB] my-1 mx-4" />
                          <button
                            onClick={async () => {
                              setShowProfileDropdown(false);
                              try {
                                const { doc: firestoreDoc, setDoc: firestoreSetDoc, getDoc: firestoreGetDoc, serverTimestamp: firestoreServerTimestamp } = require('firebase/firestore');
                                const user = auth?.currentUser;
                                if (!user) return;
                                await firestoreSetDoc(firestoreDoc(db, 'users', user.uid), { role: 'practitioner', updatedAt: firestoreServerTimestamp() }, { merge: true });
                                const practRef = firestoreDoc(db, 'practitioners', user.uid);
                                const practSnap = await firestoreGetDoc(practRef);
                                if (!practSnap.exists()) {
                                  await firestoreSetDoc(practRef, {
                                    profile: { displayName: user.displayName || user.email?.split('@')[0] || '', email: user.email, photo: user.photoURL || null },
                                    services: [], createdAt: firestoreServerTimestamp(),
                                  });
                                }
                                localStorage.setItem('userRole', 'practitioner');
                                navigate('/onboarding');
                              } catch (err) { console.error('Become practitioner failed:', err); }
                            }}
                            className="w-full px-4 py-3 text-left flex items-center space-x-3 hover:bg-[#F9F5FF] transition-colors group"
                          >
                            <Sparkles className="w-5 h-5 text-[#7c3aed] group-hover:text-[#7c3aed]" />
                            <span className="text-[#7c3aed] group-hover:text-[#7c3aed] font-medium">Become a practitioner</span>
                          </button>
                          <div className="border-t border-[#E5E7EB]">
                            <button
                              onClick={handleLogout}
                              className="w-full px-4 py-3 text-left flex items-center space-x-3 hover:bg-[#FEE2E2] transition-colors group"
                            >
                              <LogOut className="w-5 h-5 text-[#6B7280] group-hover:text-[#EF4444]" />
                              <span className="text-[#111827] group-hover:text-[#EF4444]">Sign Out</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                      // Practitioner dropdown
                      <div className="py-2">
                        {/* Navigation group */}
                        <button
                          onClick={() => {
                            navigate('/dashboard');
                            setShowProfileDropdown(false);
                          }}
                          className="w-full px-4 py-3 text-left flex items-center space-x-3 hover:bg-df-gold-wash transition-colors group"
                        >
                          <Home className="w-5 h-5 text-[#6B7280] group-hover:text-df-gold" />
                          <span className="text-[#111827] group-hover:text-df-gold">Dashboard</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/directory');
                            setShowProfileDropdown(false);
                          }}
                          className="w-full px-4 py-3 text-left flex items-center space-x-3 hover:bg-df-gold-wash transition-colors group"
                        >
                          <Globe className="w-5 h-5 text-[#6B7280] group-hover:text-df-gold" />
                          <span className="text-[#111827] group-hover:text-df-gold">Browse Directory</span>
                        </button>

                        <div className="border-t border-[#E5E7EB] my-1 mx-4" />

                        {/* Your stuff */}
                        {getQuickLinks().map((link, index) => {
                          const Icon = link.icon;
                          return (
                            <button
                              key={index}
                              onClick={() => {
                                navigate(link.path);
                                setShowProfileDropdown(false);
                              }}
                              className="w-full px-4 py-3 text-left flex items-center space-x-3 hover:bg-[#FBF7EF] transition-colors group"
                            >
                              <Icon className="w-5 h-5 text-[#6B7280] group-hover:text-[#bb9749]" />
                              <span className="text-[#111827] group-hover:text-[#bb9749]">{link.label}</span>
                            </button>
                          );
                        })}

                        {/* Marketing */}
                        <button
                          onClick={() => { navigate('/dashboard/marketing'); setShowProfileDropdown(false); }}
                          className="w-full px-4 py-3 flex items-center space-x-3 group hover:bg-[#F9F5FF] transition-colors"
                        >
                          <Megaphone className="w-5 h-5 text-[#6B7280] group-hover:text-[#bb9749]" />
                          <span className="text-[#111827] group-hover:text-[#bb9749]">Marketing</span>
                        </button>

                      {/* Footer Actions */}
                      <div className="border-t border-[#E5E7EB]">
                        <button
                          onClick={() => {
                            navigate('/dashboard/settings');
                            setShowProfileDropdown(false);
                          }}
                          className="w-full px-4 py-3 text-left flex items-center space-x-3 hover:bg-[#FBF7EF] transition-colors group"
                        >
                          <Settings className="w-5 h-5 text-[#6B7280] group-hover:text-[#bb9749]" />
                          <span className="text-[#111827] group-hover:text-[#bb9749]">Settings</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/dashboard/notifications');
                            setShowProfileDropdown(false);
                          }}
                          className="w-full px-4 py-3 text-left flex items-center space-x-3 hover:bg-[#FBF7EF] transition-colors group"
                        >
                          <Bell className="w-5 h-5 text-[#6B7280] group-hover:text-[#bb9749]" />
                          <span className="text-[#111827] group-hover:text-[#bb9749]">Notifications</span>
                        </button>
                        <button
                          onClick={() => {
                            localStorage.setItem('tour_requested', 'true');
                            navigate('/dashboard/practitioner');
                            setShowProfileDropdown(false);
                            window.location.reload();
                          }}
                          className="w-full px-4 py-3 text-left flex items-center space-x-3 hover:bg-[#FBF7EF] transition-colors group"
                        >
                          <Compass className="w-5 h-5 text-[#6B7280] group-hover:text-[#bb9749]" />
                          <span className="text-[#111827] group-hover:text-[#bb9749]">Replay Tour</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/dashboard/settings/support');
                            setShowProfileDropdown(false);
                          }}
                          className="w-full px-4 py-3 text-left flex items-center space-x-3 hover:bg-[#FBF7EF] transition-colors group"
                        >
                          <HelpCircle className="w-5 h-5 text-[#6B7280] group-hover:text-[#bb9749]" />
                          <span className="text-[#111827] group-hover:text-[#bb9749]">Help Center</span>
                        </button>
                        <div className="border-t border-[#E5E7EB]">
                          <button
                            onClick={handleLogout}
                            className="w-full px-4 py-3 text-left flex items-center space-x-3 hover:bg-[#FEE2E2] transition-colors group"
                          >
                            <LogOut className="w-5 h-5 text-[#6B7280] group-hover:text-[#EF4444]" />
                            <span className="text-[#111827] group-hover:text-[#EF4444]">Sign Out</span>
                          </button>
                        </div>
                      </div>
                      </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="df-nav-auth-btns items-center space-x-3">
                  <Link
                    to="/login"
                    className="text-black hover:text-df-gold font-medium transition-colors px-4 py-2"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/signup"
                    className="bg-df-gold hover:bg-df-gold-hover text-white px-6 py-2 rounded-lg font-semibold transition-all hover:shadow-lg transform hover:-translate-y-0.5"
                  >
                    Get Started
                  </Link>
                </div>
              )}
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="df-nav-hamburger p-2 text-black hover:text-df-gold hover:bg-df-gold-wash rounded-lg transition-all"
              >
                {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        {showMobileMenu && (
          <div className="df-mobile-menu-panel bg-white border-t border-[#E5E7EB]">
            <div className="px-4 py-3 space-y-1">
              {isAuthenticated && userInfo?.role !== 'seeker' ? (
                // Authenticated non-seeker mobile navigation (practitioners, admins, pending users)
                <>
                  <Link
                    to="/dashboard/practitioner"
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/dashboard/practitioner')
                        ? 'bg-purple-50 text-purple-600'
                        : 'text-black hover:text-df-gold hover:bg-gray-50'
                    }`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Home className="inline-block w-4 h-4 mr-2" />
                    Dashboard
                  </Link>
                  <Link
                    to="/dashboard/calendar"
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/dashboard/calendar')
                        ? 'bg-purple-50 text-purple-600'
                        : 'text-black hover:text-df-gold hover:bg-gray-50'
                    }`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    Calendar
                  </Link>
                  <Link
                    to="/dashboard/clients"
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/dashboard/clients')
                        ? 'bg-purple-50 text-purple-600'
                        : 'text-black hover:text-df-gold hover:bg-gray-50'
                    }`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Users className="inline-block w-4 h-4 mr-2" />
                    Clients
                  </Link>
                  <Link
                    to="/messages"
                    className={`block px-4 py-2 rounded-lg font-medium transition-all flex items-center justify-between ${
                      isActivePath('/messages')
                        ? 'bg-purple-50 text-purple-600'
                        : 'text-black hover:text-df-gold hover:bg-gray-50'
                    }`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <span>
                      <MessageSquare className="inline-block w-4 h-4 mr-2" />
                      Messages
                    </span>
                    {unreadCount > 0 && (
                      <span className="px-2 py-0.5 bg-purple-600 text-white text-xs font-bold rounded-full min-w-[20px] text-center">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/business-playbook"
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/business-playbook')
                        ? 'bg-purple-50 text-purple-600'
                        : 'text-black hover:text-df-gold hover:bg-gray-50'
                    }`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <BookOpen className="inline-block w-4 h-4 mr-2" />
                    Playbook
                  </Link>
                </>
              ) : isAuthenticated ? (
                // Authenticated Seeker Mobile Navigation
                <>
                  <Link
                    to="/my-sessions"
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/my-sessions')
                        ? 'bg-amber-50 text-[#bb9749]'
                        : 'text-black hover:text-df-gold hover:bg-gray-50'
                    }`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Calendar className="inline-block w-4 h-4 mr-2" />
                    My Sessions
                  </Link>
                  <Link
                    to="/directory"
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/directory')
                        ? 'bg-amber-50 text-[#bb9749]'
                        : 'text-black hover:text-df-gold hover:bg-gray-50'
                    }`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Search className="inline-block w-4 h-4 mr-2" />
                    Browse
                  </Link>
                  <Link
                    to="/my-sessions?tab=saved"
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      location.search.includes('tab=saved')
                        ? 'bg-amber-50 text-[#bb9749]'
                        : 'text-black hover:text-df-gold hover:bg-gray-50'
                    }`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Star className="inline-block w-4 h-4 mr-2" />
                    Favorites
                  </Link>
                  <Link
                    to="/learn"
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/learn')
                        ? 'bg-amber-50 text-[#bb9749]'
                        : 'text-black hover:text-df-gold hover:bg-gray-50'
                    }`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <BookOpen className="inline-block w-4 h-4 mr-2" />
                    Learn
                  </Link>
                </>
              ) : (
                // Public Mobile Navigation
                <>
                  <Link
                    to="/directory"
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/directory')
                        ? 'bg-df-gold-wash text-df-gold'
                        : 'text-black hover:text-df-gold hover:bg-gray-50'
                    }`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Users className="inline-block w-4 h-4 mr-2" />
                    Practitioners
                  </Link>
                  <Link
                    to="/pricing"
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/pricing')
                        ? 'bg-df-gold-wash text-df-gold'
                        : 'text-black hover:text-df-gold hover:bg-gray-50'
                    }`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <DollarSign className="inline-block w-4 h-4 mr-2" />
                    Pricing
                  </Link>
                  <Link
                    to="/about"
                    className={`block px-4 py-2 rounded-lg font-medium transition-all ${
                      isActivePath('/about')
                        ? 'bg-df-gold-wash text-df-gold'
                        : 'text-black hover:text-df-gold hover:bg-gray-50'
                    }`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Info className="inline-block w-4 h-4 mr-2" />
                    About
                  </Link>
                  {!isAuthenticated && (
                    <div style={{ paddingTop: '12px', marginTop: '8px', borderTop: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <Link
                        to="/login"
                        style={{ display: 'block', padding: '12px 16px', textAlign: 'center', color: '#374151', fontWeight: 500, border: '1px solid #D1D5DB', borderRadius: '8px', textDecoration: 'none' }}
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Log In
                      </Link>
                      <Link
                        to="/signup"
                        style={{ display: 'block', padding: '12px 16px', textAlign: 'center', color: 'white', fontWeight: 600, backgroundColor: '#7c3aed', borderRadius: '8px', textDecoration: 'none' }}
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Sign Up
                      </Link>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Mobile menu backdrop — z-40 sits below nav (z-50), above page content */}
      {showMobileMenu && (
        <div
          onClick={() => setShowMobileMenu(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 40,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
          }}
        />
      )}

      {/* Site-wide Paused Profile Banner - positioned below fixed navbar */}
      {isPaused && isAuthenticated && userInfo?.role === 'practitioner' && (
        <div className="pt-16">
          <PausedProfileBanner onReactivate={handleReactivate} />
        </div>
      )}

      {/* Main Content */}
      <main className={isPaused && isAuthenticated && userInfo?.role === 'practitioner' ? '' : 'pt-16'}>
        <Outlet />
      </main>

      {/* Footer */}
      <footer style={{ background: '#111', padding: '48px 32px 32px', fontFamily: "'Montserrat', sans-serif" }} className="mt-20">
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          {/* Top row */}
          <div className="df-layout-footer-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            {/* Logo + tagline */}
            <div>
              <img src={LogoWhite} alt="Divine Frameworks" style={{ height: '100px', width: 'auto' }} />
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', marginTop: '8px' }}>Where the journey begins.</p>
            </div>

            {/* Link columns */}
            <div className="df-layout-footer-columns" style={{ display: 'flex', gap: '64px' }}>
              {/* Platform */}
              <div>
                <p style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '1px' }}>Platform</p>
                <Link to="/directory" style={{ display: 'block', fontSize: '14px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', marginBottom: '10px' }} className="hover:text-white transition-colors">Browse Practitioners</Link>
                <Link to="/pricing" style={{ display: 'block', fontSize: '14px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', marginBottom: '10px' }} className="hover:text-white transition-colors">Pricing</Link>
                <Link to="/about" style={{ display: 'block', fontSize: '14px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', marginBottom: '10px' }} className="hover:text-white transition-colors">About</Link>
              </div>

              {/* Support */}
              <div>
                <p style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '1px' }}>Support</p>
                {isAuthenticated && (
                  <Link to={userInfo?.role === 'seeker' ? '/help' : '/dashboard/settings/support'} style={{ display: 'block', fontSize: '14px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', marginBottom: '10px' }} className="hover:text-white transition-colors">Help Center</Link>
                )}
                <Link to="/contact" style={{ display: 'block', fontSize: '14px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', marginBottom: '10px' }} className="hover:text-white transition-colors">Contact Us</Link>
                <Link to="/privacy" style={{ display: 'block', fontSize: '14px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', marginBottom: '10px' }} className="hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/terms" style={{ display: 'block', fontSize: '14px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', marginBottom: '10px' }} className="hover:text-white transition-colors">Terms of Service</Link>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{ marginTop: '40px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '24px' }}>
            {/* Bottom row */}
            <div className="df-layout-footer-bottom" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>&copy; 2026 Divine Frameworks. All rights reserved.</span>
              <div style={{ display: 'flex', gap: '20px' }}>
                <a href="#" aria-label="Instagram" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'color 0.2s ease', display: 'flex', alignItems: 'center' }} className="hover:text-white">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href="#" aria-label="Facebook" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'color 0.2s ease', display: 'flex', alignItems: 'center' }} className="hover:text-white">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385h-3.047v-3.47h3.047v-2.642c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953h-1.514c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385c5.738-.9 10.126-5.864 10.126-11.854z"/></svg>
                </a>
                <a href="#" aria-label="LinkedIn" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'color 0.2s ease', display: 'flex', alignItems: 'center' }} className="hover:text-white">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667h-3.554v-11.452h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zm-15.11-13.019c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019h-3.564v-11.452h3.564v11.452zm15.106-20.452h-20.454c-.979 0-1.771.774-1.771 1.729v20.542c0 .956.792 1.729 1.771 1.729h20.451c.978 0 1.774-.773 1.774-1.729v-20.542c0-.955-.796-1.729-1.774-1.729z"/></svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Footer responsive styles */}
      <style>{`
        @media (max-width: 767px) {
          .df-layout-footer-top { flex-direction: column !important; gap: 32px !important; }
          .df-layout-footer-columns { gap: 32px !important; }
          .df-layout-footer-bottom { flex-direction: column !important; text-align: center !important; gap: 16px !important; }
        }
      `}</style>

      {/* Admin Tier Override (only visible for admins) */}
      {isAuthenticated && localStorage.getItem('isAdmin') === 'true' && (
        <AdminTierOverride
          currentTier={
            localStorage.getItem('userRole') === 'seeker' ? 'seeker' :
            (localStorage.getItem('dev_subscription_tier') ||
            localStorage.getItem('userTier') ||
            'free')
          }
          onTierChange={(tier) => {
            if (tier === 'seeker') {
              // Switch to seeker view
              localStorage.setItem('dev_subscription_tier', 'free');
              localStorage.setItem('userRole', 'seeker');
              setTierOverride('free');
              console.log('🔄 Admin viewing as: CLIENT / SEEKER');
              window.dispatchEvent(new Event('storage'));
              navigate('/my-sessions');
            } else {
              // Switch to practitioner tier
              localStorage.setItem('dev_subscription_tier', tier);
              localStorage.setItem('userRole', 'practitioner');
              setTierOverride(tier);
              console.log(`🔄 Admin viewing as: ${tier.toUpperCase()}`);
              window.dispatchEvent(new Event('storage'));
              // Navigate to dashboard if currently on seeker page
              if (window.location.pathname.startsWith('/my-sessions') || window.location.pathname.startsWith('/learn')) {
                navigate('/dashboard/practitioner');
              }
            }
          }}
        />
      )}

      {/* Add CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        @keyframes slideDown {
          from { opacity: 0; max-height: 0; }
          to { opacity: 1; max-height: 500px; }
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Layout;
